import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'

interface UseTeamSharedSnapshotStatsOptions {
  teamResID?: string
  enabled?: boolean // Whether to fetch data (default: true)
}

interface SnapshotStatsData {
  usageBytes: number
  quotaBytes: number
  count: number
  slotQuota: number
}

/**
 * Hook to fetch team shared snapshot statistics
 * Uses the /team/stat/shared_snapshots endpoint to get only team shared snapshot data
 *
 * Use this hook when you only need team shared snapshot stats (e.g., SharedSnapshotView, MemberSnapshotView)
 * For complete stats including all members, use useTeamSnapshotStats instead (e.g., AdminSnapshotView)
 *
 * @param options.teamResID - Team resource ID
 * @param options.enabled - Whether to fetch data (default: true). Use this to conditionally
 *                          fetch based on permissions or other business logic.
 */
export function useTeamSharedSnapshotStats(options: UseTeamSharedSnapshotStatsOptions) {
  const { teamResID, enabled = true } = options

  const [teamShared, setTeamShared] = useState<SnapshotStatsData>({
    usageBytes: 0,
    quotaBytes: 0,
    count: 0,
    slotQuota: 0,
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchStats = useCallback(async () => {
    // Don't fetch if disabled or no team selected
    if (!enabled || !teamResID) {
      // Reset state when disabled or no team is selected
      setTeamShared({ usageBytes: 0, quotaBytes: 0, count: 0, slotQuota: 0 })
      return
    }

    setLoading(true)
    setError(null)

    try {
      const res = await API.getTeamStatSharedSnapshots({ res_id: teamResID })

      // Team shared snapshot stats
      setTeamShared({
        usageBytes: res.teamSharedSnapshotSpaceOverview.usageBytes,
        quotaBytes: res.teamSharedSnapshotSpaceOverview.quotaBytes,
        count: res.teamSharedSnapshotOverview.usage,
        slotQuota: res.teamSharedSnapshotOverview.quota,
      })
    } catch (err) {
      console.error('Failed to fetch team shared snapshot stats:', err)
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }, [enabled, teamResID])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return {
    // Team shared snapshot data
    teamShared,
    loading,
    error,
    refetch: fetchStats,
  }
}
